'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';

/**
 * Soft prompt when a new service worker took control (or is waiting).
 * Does not force reload — user chooses Update vs Later (safe mid-workout).
 */
export default function PwaUpdateBanner() {
  const t = useTranslations('PwaUpdate');
  const [visible, setVisible] = useState(false);
  const seenUpdateRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return undefined;

    let cancelled = false;
    let updateInterval = 0;

    const offerUpdate = () => {
      if (cancelled || seenUpdateRef.current) return;
      // Only prompt when an older page is already controlled (real update, not first install).
      if (!navigator.serviceWorker.controller) return;
      seenUpdateRef.current = true;
      setVisible(true);
    };

    const onControllerChange = () => {
      offerUpdate();
    };

    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    (async () => {
      try {
        const reg =
          (await navigator.serviceWorker.getRegistration()) ||
          (await navigator.serviceWorker.register('/service-worker.js'));
        if (cancelled || !reg) return;

        if (reg.waiting && navigator.serviceWorker.controller) {
          offerUpdate();
        }

        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              offerUpdate();
            }
          });
        });

        updateInterval = window.setInterval(() => {
          void reg.update().catch(() => {});
        }, 15 * 60 * 1000);
      } catch {
        // Non-fatal
      }
    })();

    return () => {
      cancelled = true;
      if (updateInterval) window.clearInterval(updateInterval);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  function dismiss() {
    setVisible(false);
  }

  function applyUpdate() {
    setVisible(false);
    window.location.reload();
  }

  if (!visible) return null;

  return (
    <div
      role="status"
      className="fixed bottom-4 left-4 right-4 z-[80] mx-auto max-w-lg rounded-2xl border border-cyan-500/40 bg-slate-950/95 px-4 py-3 shadow-xl shadow-cyan-950/40 backdrop-blur"
    >
      <p className="text-sm font-semibold text-white">{t('title')}</p>
      <p className="mt-1 text-xs text-slate-400 leading-relaxed">{t('body')}</p>
      <div className="mt-3 flex flex-wrap gap-2 justify-end">
        <button
          type="button"
          onClick={dismiss}
          className="rounded-xl border border-slate-600 px-3 py-2 text-xs font-bold text-slate-300 hover:bg-slate-800"
        >
          {t('later')}
        </button>
        <button
          type="button"
          onClick={applyUpdate}
          className="rounded-xl bg-cyan-600 px-3 py-2 text-xs font-bold text-white hover:bg-cyan-500"
        >
          {t('update')}
        </button>
      </div>
    </div>
  );
}
